import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Component } from '@/types';
import { shouldBlockSensorControl, useComponentInteraction } from '@/components/shared/rendering/InteractionContextRuntime';
import { useDeviceStatus } from '@/hooks/useDeviceStatus';
import type { DeviceBinding } from '@/components/shared/rendering/InteractionContext';
import { isHomeAssistantReviewRequired } from '@/services/homeAssistant/binding';
import { executeHomeAssistantAction, getHomeAssistantPlaceholderAction } from '@/services/homeAssistant/executeAction';
import { haStateToBoolean, useHomeAssistantEntityState } from '@/services/homeAssistant/useEntityState';
import * as LucideIcons from 'lucide-react';
import { CONTROL_THEME } from './controlThemeTokens';

// ─── Types ───────────────────────────────────────────────────────────────────

interface IconButtonThemeConfig {
  activeBg: string;
  inactiveBg: string;
  activeIconColor: string;
  inactiveIconColor: string;
  shadow: string;
  hoverBg: string;
  shape: 'circular' | 'pill' | 'rounded-rect' | 'square';
  borderColor: string;
}

interface IconButtonProps {
  component: Component;
}

// ─── Theme Presets ───────────────────────────────────────────────────────────

const THEMES: Record<string, IconButtonThemeConfig> = {
  circular: {
    activeBg: CONTROL_THEME.primary,
    inactiveBg: CONTROL_THEME.muted,
    activeIconColor: '#ffffff',
    inactiveIconColor: CONTROL_THEME.mutedForeground,
    shadow: '0 2px 4px rgba(0,0,0,0.15)',
    hoverBg: CONTROL_THEME.accent,
    shape: 'circular',
    borderColor: 'transparent',
  },
  'rounded-rect': {
    activeBg: CONTROL_THEME.primary,
    inactiveBg: CONTROL_THEME.muted,
    activeIconColor: '#ffffff',
    inactiveIconColor: CONTROL_THEME.mutedForeground,
    shadow: '0 1px 3px rgba(0,0,0,0.1)',
    hoverBg: CONTROL_THEME.accent,
    shape: 'rounded-rect',
    borderColor: 'transparent',
  },
  pill: {
    activeBg: '#10B981',
    inactiveBg: CONTROL_THEME.muted,
    activeIconColor: '#ffffff',
    inactiveIconColor: CONTROL_THEME.mutedForeground,
    shadow: '0 2px 4px rgba(0,0,0,0.12)',
    hoverBg: '#DCFCE7',
    shape: 'pill',
    borderColor: 'transparent',
  },
  ghost: {
    activeBg: 'transparent',
    inactiveBg: 'transparent',
    activeIconColor: CONTROL_THEME.primary,
    inactiveIconColor: CONTROL_THEME.mutedForeground,
    shadow: 'none',
    hoverBg: CONTROL_THEME.accent,
    shape: 'rounded-rect',
    borderColor: 'transparent',
  },
  neon: {
    activeBg: 'rgba(0,255,136,0.15)',
    inactiveBg: '#1a1a2e',
    activeIconColor: '#00ff88',
    inactiveIconColor: '#00ff8840',
    shadow: '0 0 8px rgba(0,255,136,0.3)',
    hoverBg: 'rgba(0,255,136,0.08)',
    shape: 'circular',
    borderColor: 'transparent',
  },
  material: {
    activeBg: '#1976D2',
    inactiveBg: CONTROL_THEME.muted,
    activeIconColor: '#ffffff',
    inactiveIconColor: CONTROL_THEME.mutedForeground,
    shadow: '0 2px 6px rgba(0,0,0,0.2)',
    hoverBg: CONTROL_THEME.accent,
    shape: 'rounded-rect',
    borderColor: 'transparent',
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

export const IconButton: React.FC<IconButtonProps> = ({ component }) => {
  const properties = (component.properties || {}) as Record<string, unknown>;
  const [hovered, setHovered] = useState(false);

  // ─── Theme Resolution ────────────────────────────────────────────────
  const themeName = (properties.theme as string) ?? 'circular';
  const disabled = (properties.disabled as boolean) ?? false;
  const iconName = (properties.iconName as string) || 'Power';
  const label = (properties.label as string) ?? '';
  const showLabel = (properties.showLabel as boolean) ?? false;
  const iconSize = (properties.iconSize as number) ?? 24;
  const isActive = (properties.isActive as boolean) ?? false;

  const theme = useMemo(() => {
    const base = THEMES[themeName] ?? THEMES['circular'];
    return {
      ...base,
      ...(properties.activeColor != null && { activeBg: properties.activeColor as string, activeIconColor: '#ffffff' }),
      ...(properties.inactiveColor != null && { inactiveBg: properties.inactiveColor as string }),
      ...(properties.activeIconColor != null && { activeIconColor: properties.activeIconColor as string }),
      ...(properties.inactiveIconColor != null && { inactiveIconColor: properties.inactiveIconColor as string }),
      ...(properties.borderColor != null && { borderColor: properties.borderColor as string }),
      ...(properties.hoverColor != null && { hoverBg: properties.hoverColor as string }),
    };
  }, [themeName, properties.activeColor, properties.inactiveColor, properties.activeIconColor, properties.inactiveIconColor, properties.borderColor, properties.hoverColor]);

  // ─── Icon Resolution ──────────────────────────────────────────────────
  const IconComponent = useMemo(() => {
    const icons = LucideIcons as Record<string, unknown>;
    type IconFC = React.FC<{ size?: number; color?: string }>;
    // Try exact match first, then PascalCase conversion (e.g. "zap" → "Zap")
    const resolved = (icons[iconName] ?? icons[iconName.charAt(0).toUpperCase() + iconName.slice(1)]) as IconFC | undefined;
    if (resolved) return resolved;
    // Fallback to Power icon
    return icons['Power'] as IconFC | undefined;
  }, [iconName]);

  // ─── Interaction ─────────────────────────────────────────────────────
  const { handlers, mode } = useComponentInteraction(component);
  const isViewerMode = mode === 'functional';
  const sensorControlBlocked = shouldBlockSensorControl(component);

  const sensorId = useMemo(() => {
    const action = properties.action;
    if (typeof action === 'object' && action !== null) {
      return (action as Record<string, unknown>).deviceId as string || component.id;
    }
    return component.id;
  }, [properties.action, component.id]);

  const deviceBinding = useMemo(() => {
    const action = properties.action;
    if (typeof action === 'object' && action !== null) {
      return (action as Record<string, unknown>).deviceBinding as DeviceBinding | undefined;
    }
    return undefined;
  }, [properties.action]);

  const deviceStatus = useDeviceStatus(deviceBinding);
  const deviceIsOn = deviceStatus?.isOn;
  const homeAssistantAction = getHomeAssistantPlaceholderAction(component);
  const canExecuteHomeAssistantAction = Boolean(
    homeAssistantAction &&
      !isHomeAssistantReviewRequired(homeAssistantAction.entityId, homeAssistantAction.domain),
  );
  const haLiveState = useHomeAssistantEntityState(component);
  const haIsOn = haLiveState ? haStateToBoolean(haLiveState.state) : null;

  const [localActive, setLocalActive] = useState(isActive);

  useEffect(() => {
    if (haIsOn !== null) {
      setLocalActive(haIsOn);
      return;
    }
    if (deviceIsOn !== undefined) setLocalActive(deviceIsOn);
  }, [haIsOn, deviceIsOn]);

  const effectiveActive = isViewerMode
    ? (deviceStatus ? deviceStatus.isOn : localActive)
    : isActive;
  const canActivate = !disabled && (
    Boolean(deviceBinding && !sensorControlBlocked) ||
    Boolean(isViewerMode && canExecuteHomeAssistantAction) ||
    Boolean(isViewerMode && !sensorControlBlocked && handlers.onSensorControl)
  );

  const handleClick = useCallback(async () => {
    if (disabled) return;
    const newState = !effectiveActive;

    if (isViewerMode && canExecuteHomeAssistantAction) {
      setLocalActive(newState);
      try {
        await executeHomeAssistantAction(component, {
          value: newState,
          parameters: { command: 'set', property: 'isActive' },
        });
      } catch {
        setLocalActive(effectiveActive);
      }
      return;
    }

    if (sensorControlBlocked) return;

    // Device-bound: control physical device directly (works in editor + viewer)
    if (deviceBinding) {
      setLocalActive(newState);
      const { executeDeviceCommand } = await import('@/integrations/kasa/deviceCommandMapper');
      executeDeviceCommand(
        { sensorId, action: 'control', parameters: { command: 'set', property: 'isActive', value: newState } },
        deviceBinding
      );
      return;
    }

    // Non-bound: only works in viewer mode
    if (!isViewerMode || !handlers.onSensorControl) return;
    setLocalActive(newState);
    handlers.onSensorControl({
      sensorId,
      action: 'control',
      parameters: { command: 'set', value: newState, property: 'isActive' },
    });
  }, [
    disabled,
    isViewerMode,
    canExecuteHomeAssistantAction,
    component,
    effectiveActive,
    sensorControlBlocked,
    deviceBinding,
    handlers,
    sensorId,
  ]);

  // ─── Computed Styles ──────────────────────────────────────────────────
  const bgColor = effectiveActive ? theme.activeBg : (hovered && isViewerMode ? theme.hoverBg : theme.inactiveBg);
  const iconColor = effectiveActive ? theme.activeIconColor : theme.inactiveIconColor;
  const shape = (properties.shape as string) ?? theme.shape;
  const borderRadius = shape === 'circular' ? '50%'
    : shape === 'pill' ? '999px'
    : shape === 'square' ? '8px'
    : '12px';

  // Shape layout: each shape has a visually distinct proportion
  const shapeLayout = shape === 'pill'
    ? { width: 'calc(100% - 4px)', height: '60%' }           // wide + short stadium
    : shape === 'rounded-rect'
    ? { width: 'calc(100% - 4px)', height: 'calc(100% - 4px)' } // fills container, rounded corners
    : { height: 'calc(100% - 4px)', aspectRatio: '1 / 1', maxWidth: 'calc(100% - 4px)' }; // square (circle/square)

  const buttonStyle: React.CSSProperties = {
    ...shapeLayout,
    borderRadius,
    backgroundColor: bgColor,
    boxShadow: effectiveActive ? theme.shadow : 'none',
    ...(theme.borderColor !== 'transparent' ? { border: `2px solid ${theme.borderColor}` } : {}),
    cursor: canActivate ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: effectiveActive ? 'scale(1.05)' : 'scale(1)',
    overflow: 'hidden',
  };

  return (
    <div
      data-testid="icon-button"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        gap: 4,
        minWidth: 0,
        minHeight: 0,
        padding: 4,
        boxSizing: 'border-box',
        userSelect: 'none',
        overflow: 'visible',
      }}
    >
      <div
        role="button"
        aria-pressed={effectiveActive}
        aria-disabled={!canActivate}
        aria-label={label || iconName}
        tabIndex={canActivate && isViewerMode ? 0 : -1}
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && canActivate && isViewerMode) {
            e.preventDefault();
            handleClick();
          }
        }}
        style={buttonStyle}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '60%', height: '60%' }}>
          {IconComponent ? (
            <IconComponent size={iconSize} color={iconColor} />
          ) : (
            <span style={{ fontSize: iconSize, color: iconColor, fontWeight: 700 }}>?</span>
          )}
        </div>
      </div>

      {showLabel && label && (
        <span style={{
          fontSize: 11,
          color: CONTROL_THEME.mutedForeground,
          textAlign: 'center',
          lineHeight: 1.2,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {label}
        </span>
      )}
    </div>
  );
};
